import dayjs from "dayjs";

import { swSettings } from "app/common/services/swSettings";
import { IntentCell } from "app/components/React/Table/cells/IntentCell";
import { KeywordDifficultyCell } from "app/components/React/Table/cells/KeywordDifficultyCell";
import { THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE } from "app/filters/const";
import { i18nFilter } from "app/filters/i18nFilter";
import { minAbbrNumberFilter } from "app/filters/ngFilters";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService, { BasicDurations } from "app/services/DurationService";

import CoreTrendsBarCell from "pro-features/components/core cells/src/CoreTrendsBarCell/CoreTrendsBarCell";
import type { ITrendsBarValue } from "pro-features/components/TrendsBar/src/TrendsBar";
import {
    DefaultCellRightAlign,
    IndexCell,
    LeadingSite,
    ProgressBarPercentCell,
    SearchKeywordCell,
} from "../../../../../../components/React/Table/cells";
import { RowSelectionConsumer } from "../../../../../../components/React/Table/cells/RowSelection";
import {
    DefaultCellHeader,
    DefaultCellHeaderRightAlign,
    HeaderCellBlank,
} from "../../../../../../components/React/Table/headerCells";
import { SelectAllRowsHeaderCellConsumer } from "../../../../../../components/React/Table/headerCells/SelectAllRowsHeaderCell";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

const getVolumeDisplayNameKey = (isWindowDuration: boolean) =>
    isWindowDuration
        ? "keyword.generator.tool.table.column.monthly.volume"
        : "keyword.generator.tool.table.table.column.volume";

const DEFAULT_SORT_DIRECTION = "desc";
const dateFormat = (date) => dayjs(date).format("MMM, YYYY");
export const PhraseMatchTableSettings = {
    defaultSortField: (duration) =>
        duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS ? "windowVolume" : "monthlyVolume",
    defaultSortDirection: DEFAULT_SORT_DIRECTION,
    getColumns: (sortedColumn: { field: string; sortDirection: string }, webSource: string, country, duration) => {
        const durationObject = DurationService.getDurationData(duration);
        const isWindowDuration = duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
        const volumeDisplayNameKey = getVolumeDisplayNameKey(isWindowDuration);
        return [
            {
                fixed: true,
                cellComponent: RowSelectionConsumer,
                sortable: false,
                width: 48,
                headerComponent: SelectAllRowsHeaderCellConsumer,
                isResizable: false,
                disableHeaderCellHover: true,
            },
            {
                fixed: true,
                cellComponent: IndexCell,
                disableHeaderCellHover: true,
                headerComponent: HeaderCellBlank,
                sortable: false,
                width: 65,
                isResizable: false,
            },
            {
                fixed: true,
                field: "keyword",
                sortable: true,
                showTotalCount: true,
                cellComponent: (props) => (
                    <SearchKeywordCell
                        {...props}
                        destinationState={
                            swSettings.components.KeywordAnalysisV2Organic
                                ? "organicsearch_keywordAnalysis_2"
                                : undefined
                        }
                        destinationStateParams={{ selectedPageTab: EKeywordTabs[EKeywordTabs.Total] }}
                        adsUrl={proNavigator.href("paidsearch_keywordAnalysis_serp_ads", {
                            webSource,
                            country,
                            duration,
                            keyword: props.value,
                        })}
                    />
                ),
                displayName: i18nFilter()("keyword.generator.tool.table.column.keyword"),
                tooltip: i18nFilter()("keyword.generator.tool.table.column.keyword.tooltip"),
                width: 258,
                groupKey: "keywordGeneratorTool",
            },
            isWindowDuration
                ? {
                      field: "windowVolume",
                      sortable: true,
                      displayName: i18nFilter()("keyword.generator.tool.table.table.column.daily.volume"),
                      tooltip: i18nFilter()("keyword.generator.tool.table.column.daily.volume.tooltip"),
                      cellComponent: DefaultCellRightAlign,
                      format: "minAbbrNumber:50",
                      headerComponent: DefaultCellHeaderRightAlign,
                      minWidth: 130,
                      groupKey: "keywordGeneratorTool",
                  }
                : {
                      field: "monthlyVolume",
                      sortable: true,
                      displayName: i18nFilter()(volumeDisplayNameKey),
                      tooltip: i18nFilter()("keyword.generator.tool.table.column.volume.tooltip"),
                      cellComponent: DefaultCellRightAlign,
                      headerComponent: DefaultCellHeaderRightAlign,
                      format: "minAbbrNumber:50",
                      minWidth: 140,
                      groupKey: "keywordGeneratorTool",
                  },

            {
                field: "averageVolume",
                displayName: i18nFilter()("website.keywords.2.0.phrase.match.table.window.average.volume.column"),
                tooltip: i18nFilter()("website.keywords.2.0.phrase.match.table.window.average.volume.column.tooltip"),
                cellComponent: DefaultCellRightAlign,
                headerComponent: DefaultCellHeaderRightAlign,
                format: "minAbbrNumber:50",
                width: 100,
                groupKey: "keywordGeneratorToolRelated",
            },
            {
                field: "volumeTrend",
                displayName: i18nFilter()("keyword.generator.tool.table.column.volumetrend"),
                tooltip: i18nFilter()("keyword.generator.tool.table.column.volumetrend.tooltip"),
                cellComponent: ({ value }) => {
                    if (value) {
                        const data = Object.keys(value)
                            .sort()
                            .map((item) => ({
                                value: value[item],
                                tooltip: (
                                    <span>
                                        <strong>
                                            {minAbbrNumberFilter()(value[item], THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE)}
                                        </strong>
                                        {` searches in ${dateFormat(item)}`}
                                    </span>
                                ),
                            })) as ITrendsBarValue[];
                        return <CoreTrendsBarCell value={data} />;
                    } else {
                        return "N/A";
                    }
                },
                format: "swPosition",
                width: 130,
                groupKey: "keywordGeneratorTool",
            },
            {
                field: "latestZeroClicks",
                sortable: true,
                displayName: i18nFilter()("keyword.generator.tool.table.column.zero.click"),
                tooltip: i18nFilter()("keyword.generator.tool.table.column.zero.click.tooltip"),
                cellComponent: (props) => <ProgressBarPercentCell value={props.value} />,
                width: 170,
            },
            {
                field: "difficulty",
                automationKey: "Difficulty",
                trackingName: "Keyword Difficulty",
                displayName: i18nFilter()("analysis.source.search.all.table.columns.keyword.difficulty.title"),
                tooltip: i18nFilter()("analysis.source.search.all.table.columns.keyword.difficulty.tooltip", {
                    date: durationObject.raw.to.format("MMM YYYY"),
                }),
                width: 68,
                cellComponent: KeywordDifficultyCell,
                sortable: true,
                groupKey: "keywordGeneratorTool",
            },
            {
                field: "intents",
                trackingName: "Keyword Intent",
                displayName: i18nFilter()("keywords.table.header.intent.display.name"),
                tooltip: i18nFilter()("keywords.table.header.intent.tooltip"),
                width: 120,
                cellComponent: IntentCell,
            },
            {
                field: "cpc",
                sortable: true,
                displayName: i18nFilter()("keyword.generator.tool.table.column.cpc"),
                tooltip: i18nFilter()("keyword.generator.tool.table.column.cpc.tooltip"),
                cellComponent: DefaultCellRightAlign,
                headerComponent: DefaultCellHeaderRightAlign,
                format: "CPC",
                width: 86,
                groupKey: "keywordGeneratorTool",
            },
            {
                groupKey: "keywordGeneratorTool",
                width: 200,
                sortable: false,
                field: "leadingSite",
                displayName: i18nFilter()("keyword.generator.tool.table.column.website"),
                tooltip: i18nFilter()("keyword.generator.tool.table.column.website.tooltip"),
                cellComponent: LeadingSite,
            },
        ]
            .filter(Boolean)
            .map((col) => {
                const columnSort = sortedColumn || {
                    field: isWindowDuration ? "windowVolume" : "monthlyVolume",
                    sortDirection: DEFAULT_SORT_DIRECTION,
                };
                const isSorted = columnSort && col.field === columnSort.field;
                return {
                    visible: true,
                    headerComponent: DefaultCellHeader,
                    isSorted,
                    sortDirection: isSorted ? columnSort.sortDirection : DEFAULT_SORT_DIRECTION,
                    ...col,
                };
            });
    },
};
